/**
 * SizilPro — CompanyStep
 * Step 1 of setup: company name, address, contact info.
 *
 * Updated for a more guided “popcorn question” experience while keeping
 * the same save contract and current setup flow intact.
 */

import { useMemo } from "react";
import type { ReactNode } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Building2,
  Loader2,
  MapPin,
  Phone,
  Mail,
  Globe,
  Sparkles,
  FileText,
} from "lucide-react";
import { useBrandSettings, useUpsertBrandSettings } from "../../hooks/useBrandSettings";
import {
  SetupFieldGroup,
  SetupInput,
  SetupLabel,
  SetupActions,
} from "./SetupPrimitives";

const schema = z.object({
  company_name: z.string().min(1, "Company name is required"),
  phone: z.string().optional(),
  email: z.string().email("Enter a valid email").optional().or(z.literal("")),
  website: z.string().optional(),
  address_line1: z.string().optional(),
  address_line2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().optional(),
  footer_text: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

interface Props {
  onComplete: () => void;
}

function QuestionCard({
  number,
  title,
  description,
  children,
}: {
  number: number;
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 md:p-6">
      <div className="mb-5 flex items-start gap-4">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-sm font-semibold text-black">
          {number}
        </div>
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-white">{title}</h3>
          {description ? (
            <p className="mt-1 text-sm leading-6 text-white/45">{description}</p>
          ) : null}
        </div>
      </div>
      {children}
    </section>
  );
}

function PreviewBullets({ values }: { values: FormValues }) {
  const lines = useMemo(() => {
    const addressLine = [
      values.address_line1,
      values.address_line2,
      [values.city, values.state].filter(Boolean).join(", "),
      values.zip,
    ]
      .filter(Boolean)
      .join(" ");

    return [
      values.company_name || "Your company name",
      values.phone || "Phone not added yet",
      values.email || "Email not added yet",
      values.website || "Website not added yet",
      addressLine || "Business address not added yet",
    ];
  }, [values]);

  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
      <p className="text-[10px] uppercase tracking-[0.18em] text-white/35">
        What updates in the preview
      </p>
      <ul className="mt-3 space-y-2 text-sm text-white/75">
        {lines.map((line, idx) => (
          <li key={`${line}-${idx}`} className="flex items-start gap-2">
            <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-white/40" />
            <span>{line}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ContextCard() {
  return (
    <div className="rounded-2xl border border-amber-500/15 bg-amber-500/5 p-4">
      <div className="flex items-start gap-3">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
        <div>
          <p className="text-sm font-medium text-amber-300">
            This step becomes your proposal foundation
          </p>
          <p className="mt-1 text-sm leading-6 text-white/45">
            The company details you enter here will flow into brand setup preview,
            future templates, and final proposals. Keep it simple and accurate first.
          </p>
        </div>
      </div>
    </div>
  );
}

function HandoffCard() {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
      <div className="flex items-start gap-3">
        <FileText className="mt-0.5 h-4 w-4 shrink-0 text-white/35" />
        <div>
          <p className="text-sm font-medium text-white/80">What comes next</p>
          <p className="mt-1 text-sm leading-6 text-white/45">
            After this, you’ll move into branding, cover letter, reusable pages,
            and services. That full setup becomes the starting point for templates
            and every proposal you generate later.
          </p>
        </div>
      </div>
    </div>
  );
}

export function CompanyStep({ onComplete }: Props) {
  const { data: brand, isLoading } = useBrandSettings();
  const upsert = useUpsertBrandSettings();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: {
      company_name: brand?.company_name ?? "",
      phone: brand?.phone ?? "",
      email: brand?.email ?? "",
      website: brand?.website ?? "",
      address_line1: brand?.address_line1 ?? "",
      address_line2: brand?.address_line2 ?? "",
      city: brand?.city ?? "",
      state: brand?.state ?? "",
      zip: brand?.zip ?? "",
      footer_text: brand?.footer_text ?? "",
    },
  });

  const values = watch();

  const onSubmit = async (formValues: FormValues) => {
    await upsert.mutateAsync({
      ...formValues,
      company_name: formValues.company_name.trim(),
      phone: formValues.phone?.trim() || null,
      email: formValues.email?.trim() || null,
      website: formValues.website?.trim() || null,
      address_line1: formValues.address_line1?.trim() || null,
      address_line2: formValues.address_line2?.trim() || null,
      city: formValues.city?.trim() || null,
      state: formValues.state?.trim() || null,
      zip: formValues.zip?.trim() || null,
      footer_text: formValues.footer_text?.trim() || null,
    });
    onComplete();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-5 w-5 animate-spin text-white/30" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="mb-2">
        <p className="text-sm leading-6 text-white/55">
          Let’s start with the basics. This information becomes the company
          foundation for your preview, templates, proposals, and PDF output.
        </p>
      </div>

      <ContextCard />

      <QuestionCard
        number={1}
        title="What is the company name?"
        description="This is the main brand name shown across the proposal cover and header areas."
      >
        <SetupFieldGroup>
          <div className="relative">
            <Building2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" />
            <SetupInput
              id="company_name"
              placeholder="Hatch Cleaning Co."
              error={errors.company_name?.message}
              className="pl-10"
              {...register("company_name")}
            />
          </div>
        </SetupFieldGroup>
      </QuestionCard>

      <QuestionCard
        number={2}
        title="How should clients contact you?"
        description="Add the main phone number, email, and website you want shown on proposals."
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <SetupLabel htmlFor="phone">Phone</SetupLabel>
            <div className="relative">
              <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" />
              <SetupInput
                id="phone"
                type="tel"
                placeholder="(615) 555-0100"
                className="pl-10"
                {...register("phone")}
              />
            </div>
          </div>

          <div>
            <SetupLabel htmlFor="email">Main email</SetupLabel>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" />
              <SetupInput
                id="email"
                type="email"
                placeholder="hello@yourcompany.com"
                error={errors.email?.message}
                className="pl-10"
                {...register("email")}
              />
            </div>
          </div>
        </div>

        <div className="mt-4">
          <SetupLabel htmlFor="website">Website</SetupLabel>
          <div className="relative">
            <Globe className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" />
            <SetupInput
              id="website"
              placeholder="https://yourcompany.com"
              className="pl-10"
              {...register("website")}
            />
          </div>
        </div>
      </QuestionCard>

      <QuestionCard
        number={3}
        title="What business address should appear on the proposal?"
        description="This is commonly shown on the cover page and in company contact sections."
      >
        <SetupFieldGroup>
          <div className="relative">
            <MapPin className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-white/30" />
            <SetupInput
              placeholder="123 Main Street"
              className="pl-10"
              {...register("address_line1")}
            />
          </div>

          <div className="mt-3">
            <SetupInput
              placeholder="Suite 400 (optional)"
              {...register("address_line2")}
            />
          </div>

          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-5">
            <div className="md:col-span-3">
              <SetupInput placeholder="City" {...register("city")} />
            </div>
            <div>
              <SetupInput placeholder="ST" {...register("state")} />
            </div>
            <div>
              <SetupInput placeholder="ZIP" {...register("zip")} />
            </div>
          </div>
        </SetupFieldGroup>
      </QuestionCard>

      <QuestionCard
        number={4}
        title="Do you want a footer message on every proposal page?"
        description="Optional. Great for adding a support contact, short note, or CTA."
      >
        <SetupFieldGroup>
          <SetupInput
            placeholder="Questions? Contact Sarah at sarah@hatchcleaning.com"
            {...register("footer_text")}
          />
        </SetupFieldGroup>
      </QuestionCard>

      <PreviewBullets values={values} />
      <HandoffCard />

      <SetupActions>
        <button
          type="submit"
          disabled={upsert.isPending}
          className="flex items-center gap-2 rounded-lg bg-white px-6 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-white/90 disabled:opacity-50"
        >
          {upsert.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          Save & continue
        </button>
      </SetupActions>
    </form>
  );
}